// Rendered surfaces are the acknowledgement of a UI command. State flags alone
// can describe a requested render that has not happened (or was cancelled).
const priority = ['save', 'seller_review', 'showing', 'email', 'comparison', 'preferences', 'map', 'nearby', 'staging', 'photos', 'property'];
const labels = {save:'the save form',seller_review:'the selling inquiry form',showing:'the showing form',email:'the email form',comparison:'the comparison',preferences:'your preferences',map:'the map',nearby:'nearby places',staging:'the staging choices',photos:'the property photo',property:'the property details',results:'your search results',favorites:'your saved homes',chat:'the chat'};
const closeCommands = {save:'cancel_save',seller_review:'cancel_seller_review',showing:'cancel_showing',email:'cancel_email',comparison:'close_compare',preferences:'close_preferences',map:'close_map',nearby:'close_nearby',staging:'close_staging',photos:'close_photos',property:'close_property'};
export function surfaceVisible(element) {
  if (!element?.isConnected || !element.getClientRects().length) return false;
  for (let node = element; node instanceof Element; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (node.hidden || node.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
  }
  return true;
}
export function readAssistantScreen(context = {}) {
  const surfaces = [...document.querySelectorAll('[data-assistant-surface]')].filter(surfaceVisible);
  // Prefer a surface that actually receives pointer input. This catches forms
  // covered by another portal, even when both have open=true in React state.
  const exposed = surfaces.filter(element => {
    if (typeof document.elementFromPoint !== 'function') return true;
    const rect = element.getBoundingClientRect();
    const x = Math.max(1, Math.min(window.innerWidth - 1, rect.left + rect.width / 2));
    const y = Math.max(1, Math.min(window.innerHeight - 1, rect.top + Math.min(rect.height / 2, 160)));
    const hit = document.elementFromPoint(x, y);
    return hit && element.contains(hit);
  });
  const top = priority.find(kind => exposed.some(el => el.dataset.assistantSurface === kind));
  return {screen:top || (surfaces.length ? 'unknown' : context.visible_count ? context.results_mode === 'favorites' ? 'favorites' : 'results' : 'chat'), surfaces:surfaces.map(el => el.dataset.assistantSurface)};
}
export const screenCloseCommand = screen => closeCommands[screen] || null;
export const screenLabel = screen => labels[screen] || 'the current screen';
export async function waitForScreen(check, isCurrent = () => true, timeout = 1800) {
  const start = Date.now();
  // Let React commit, run state callbacks, and paint before acknowledging.
  do {
    await new Promise(resolve => setTimeout(resolve, 32));
    if (!isCurrent()) return false;
    if (check()) return true;
  } while (Date.now() - start < timeout);
  return false;
}
const opens = {open_map:'map',open_nearby:'nearby',open_property:'property',open_photos:'photos',show_room:'photos',open_staging:'staging',open_showing:'showing',update_showing:'showing',submit_showing:'showing',prepare_email:'email',open_seller_review:'seller_review',update_seller_review:'seller_review',compare_properties:'comparison',show_preferences:'preferences'};
const closes = Object.fromEntries(Object.entries(closeCommands).map(([screen,command]) => [command,screen]));
export async function acknowledgeScreenAction({command, result, getContext, isCurrent = () => true}) {
  const context = () => getContext();
  const current = () => readAssistantScreen(context());
  const target = opens[command];
  const closing = closes[command];
  const returning = ['return_to_property','return_to_results','show_results'].includes(command);
  const saving = ['save_property','update_save_contact','confirm_save','get_save_status'].includes(command);
  if (!target && !closing && !returning && !saving) return result;
  const complete = () => {
    const ui = context(), screen = current();
    if (saving) {
      if (ui.save_open) return screen.screen === 'save';
      // A remembered-contact save or a previously completed/manual save needs
      // no new form. Never equate save_ready=false on a closed form with missing fields.
      return command === 'get_save_status' || ['saved','pending','removed'].includes(ui.save_status) || ui.selected_saved;
    }
    const propertyOnly = screen.screen === 'property' && screen.surfaces.every(surface => surface === 'property');
    if (returning) return command === 'return_to_property' ? propertyOnly : ['results','favorites','chat'].includes(screen.screen);
    if (closing) return !screen.surfaces.includes(closing);
    return target === 'property' ? propertyOnly : screen.screen === target;
  };
  if (!(await waitForScreen(complete,isCurrent))) {
    if (!isCurrent()) return 'The screen changed while that request was running. Use the current screen for your next request.';
    // Keep actionable failures instead of replacing them with a success claim.
    if (/^(?:Which property|Choose |Open a property|Please reopen|Select at least|No photos|No labeled|The photos are still|This home does not|Your comparison tray|The property save is still|This home is saved in this browser)/.test(result || '')) return result;
    return `I could not confirm that screen change. You are still on ${screenLabel(current().screen)}. I cannot confirm the requested action completed.`;
  }
  if (closing || returning) {
    const note = command === 'cancel_save' ? ' Your existing favorites are unchanged.' : command === 'cancel_showing' ? ' Any request already sent remains with the agent.' : command === 'close_compare' ? ' Your comparison selections are kept.' : '';
    return `Back to ${screenLabel(current().screen)}.${note}`;
  }
  return result;
}
